import { useQuery } from '@tanstack/react-query';

// "Report a problem" (TF-173): Jira's issue collector, where a server has one (ISSUE_COLLECTOR_URL; the
// web server answers /issue-collector.json, or 404 when there is none). Its script is loaded only when
// somebody asks to report something, never with the app, and the form it opens is Jira's own, in a
// frame from Jira. What goes with a report: what the person types, the page they were on, and what the
// form is filled with - their name and address, so the report can be answered, and the app's state in
// a line (page, language, time zone, screen) for whoever looks into it.

interface CollectorProps {
  triggerFunction: (show: () => void) => void;
  fieldValues: Record<string, string>;
}

declare global {
  interface Window {
    ATL_JQ_PAGE_PROPS?: CollectorProps;
  }
}

/** The collector's script address when this server has one; null otherwise. Asked once a session. */
export function useIssueCollector(): string | null {
  const config = useQuery({
    queryKey: ['issue-collector'],
    queryFn: async () => {
      const response = await fetch('/issue-collector.json', { headers: { accept: 'application/json' } });
      if (!response.ok) return null;
      const body = (await response.json()) as { url?: unknown };
      return typeof body.url === 'string' && body.url.startsWith('https://') ? body.url : null;
    },
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  return config.data ?? null;
}

let shown: Promise<() => void> | null = null;
const LOAD_MS = 15_000;

// Jira calls triggerFunction only when the collector's trigger is "Custom" in Jira. With the default
// trigger it puts its own "Provide feedback" tab on the page instead and never calls ours, so the
// form waited for a click on that tab and then gave up (TEST, 12 October 2026). The tab is hidden
// (global.css) and clicked for the person; either way the form opens from "Report a problem".
const DEFAULT_TRIGGER = '#atlwdg-trigger, .atlwdg-trigger';
const defaultTrigger = () => document.querySelector<HTMLElement>(DEFAULT_TRIGGER);

/** The collector's id from its script address (collectorId=...), under which Jira also looks up the page's settings. */
const collectorIdOf = (url: string): string | null => {
  try {
    return new URL(url).searchParams.get('collectorId');
  } catch {
    return null;
  }
};

/**
 * Jira's form, opened. The first time the script is loaded, with the form's fields filled in; it hands
 * back the function that opens the form (its "custom trigger"), kept for the next time. Rejects when
 * the script cannot be had (blocked, offline); the next try loads it again.
 */
export function openIssueCollector(url: string, fieldValues: Record<string, string>): Promise<void> {
  const collectorId = collectorIdOf(url);
  const props = window.ATL_JQ_PAGE_PROPS as (CollectorProps & Record<string, unknown>) | undefined;
  if (props) {
    props.fieldValues = fieldValues;
    if (collectorId && props[collectorId]) (props[collectorId] as CollectorProps).fieldValues = fieldValues;
  }
  if (!shown) {
    shown = new Promise<() => void>((resolve, reject) => {
      let watcher: MutationObserver | null = null;
      const settle = (outcome: () => void) => {
        window.clearTimeout(timer);
        watcher?.disconnect();
        outcome();
      };
      const timer = window.setTimeout(() => settle(() => reject(new Error('The issue collector did not load'))), LOAD_MS);
      const settings: CollectorProps = { triggerFunction: (show) => settle(() => resolve(show)), fieldValues };
      // Both places Jira reads: the top level for one collector, under its id when a page has several.
      window.ATL_JQ_PAGE_PROPS = { ...settings, ...(collectorId ? { [collectorId]: settings } : {}) };
      const tabFound = () => {
        if (!defaultTrigger()) return false;
        settle(() => resolve(() => defaultTrigger()?.click()));
        return true;
      };
      watcher = new MutationObserver(() => void tabFound());
      watcher.observe(document.body, { childList: true, subtree: true });
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onerror = () => settle(() => reject(new Error('The issue collector did not load')));
      document.head.append(script);
    }).catch((error: unknown) => {
      shown = null;
      document.querySelectorAll(`script[src="${CSS.escape(url)}"]`).forEach((script) => script.remove());
      throw error;
    });
  }
  return shown.then((show) => show());
}

/** The app's state in one line, for whoever looks into a report: where, not what is on the page. */
export function environmentLine(details: { path: string; language: string; zone: string; width: number; height: number; userAgent: string }): string {
  return `DoCuLat web, page ${details.path}, language ${details.language}, time zone ${details.zone}, window ${details.width}x${details.height}, ${details.userAgent}`;
}
